import { useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, CheckCircle2, Camera, Loader2, Trash2, Wrench, ShieldCheck,
} from "lucide-react";

// Maintenance log flow shown after the operator scans a non-temperature
// equipment QR code (e.g. Band Saw). The date/time and the "performed
// by" name are captured automatically by the backend from the request
// timestamp and the authenticated user, mirroring the hygiene &
// cleanliness pattern — operators only fill Reason + optional notes /
// photos. Supervisor verification is a separate downstream step
// (existing PATCH `/maintenance/logs/{id}/verify`).
const REASON_OPTIONS = [
  { value: "scheduled", label: "Scheduled" },
  { value: "wear", label: "Wear" },
  { value: "break", label: "Breakdown" },
  { value: "tracking_tension", label: "Tracking & tension verified" },
  { value: "sanitation", label: "Sanitation" },
];

function pad(n) { return String(n).padStart(2, "0"); }
function todayDate() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function nowTime() {
  const d = new Date();
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Small Yes / No verification block used for the sanitation and
// tracking-tension checks. Mirrors the light-touch pattern from the
// daily hygiene form: two large pills, "Yes" preselected, and a
// required reason textarea when the operator flips to "No".
function YesNoCheck({ testid, label, help, value, onChange, issue, onIssueChange }) {
  return (
    <div>
      <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
        style={{ color: "var(--vb-text-2)" }}>{label}</label>
      {help && (
        <p className="text-[11px] opacity-70 -mt-1 mb-2">{help}</p>
      )}
      <div className="grid grid-cols-2 gap-2">
        {[
          { v: true,  txt: "Yes" },
          { v: false, txt: "No"  },
        ].map((opt) => {
          const active = value === opt.v;
          return (
            <button
              key={opt.txt}
              type="button"
              onClick={() => onChange(opt.v)}
              data-testid={`maintenance-${testid}-${opt.txt.toLowerCase()}`}
              className="rounded-lg px-3 py-2 text-sm font-medium border"
              style={{
                borderColor: active
                  ? (opt.v ? "var(--vb-success, #2f855a)" : "var(--vb-danger, #c53030)")
                  : "var(--vb-border, rgba(0,0,0,0.15))",
                background: active
                  ? (opt.v ? "rgba(47,133,90,0.10)" : "rgba(197,48,48,0.10)")
                  : "transparent",
                color: active
                  ? (opt.v ? "var(--vb-success, #2f855a)" : "var(--vb-danger, #c53030)")
                  : "var(--vb-text-2)",
                cursor: "pointer",
              }}
            >
              {opt.txt}
            </button>
          );
        })}
      </div>
      {!value && (
        <textarea
          className="vb-input mt-2"
          rows={2}
          required
          placeholder="Describe the issue and what caused it *"
          value={issue}
          onChange={(e) => onIssueChange(e.target.value)}
          data-testid={`maintenance-${testid}-issue`}
        />
      )}
    </div>
  );
}

export default function MaintenanceLogForm({
  equipmentId, equipmentName, siteName, facilityName, serialNumber,
  onBack, onRescan, onHome,
}) {
  const { user } = useAuth();
  const [reason, setReason] = useState("scheduled");
  // Date/time defaults to "now" so most operators can just submit, but
  // stays editable — someone logging a job they finished earlier in
  // the shift can dial it back.
  const [date, setDate] = useState(todayDate());
  const [time, setTime] = useState(nowTime());
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  // Post-maintenance verification checklist — defaults to Yes so an
  // all-clear submit is one tap. A "No" requires a reason.
  const [sanitationOk, setSanitationOk] = useState(true);
  const [sanitationIssue, setSanitationIssue] = useState("");
  const [tensionOk, setTensionOk] = useState(true);
  const [tensionIssue, setTensionIssue] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/cleaning/upload-photo?kind=maintenance", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotos((prev) => [...prev, data.url]);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Photo upload failed.");
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!sanitationOk && !sanitationIssue.trim()) {
      setErr("Sanitation is marked 'No' — please describe the issue.");
      return;
    }
    if (!tensionOk && !tensionIssue.trim()) {
      setErr("Tracking & tension is marked 'No' — please describe the issue.");
      return;
    }
    setBusy(true);
    try {
      // Combine the operator-adjustable local date + time into an ISO
      // timestamp. `performed_by` is still stamped by the backend from
      // the authenticated user so no impersonation is possible.
      const performed_at = new Date(`${date}T${time}:00`).toISOString();
      await api.post("/maintenance/logs", {
        equipment_id: equipmentId,
        reason,
        performed_at,
        notes: notes.trim(),
        photos,
        sanitation_ok: sanitationOk,
        sanitation_issue: sanitationOk ? "" : sanitationIssue.trim(),
        tracking_tension_ok: tensionOk,
        tracking_tension_issue: tensionOk ? "" : tensionIssue.trim(),
      });
      setSuccess(true);
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Could not save maintenance log.");
    } finally { setBusy(false); }
  };

  if (success) {
    return (
      <div className="max-w-lg vb-fade-in" data-testid="maintenance-success">
        <div className="vb-card p-5">
          <div className="vb-pill ok mb-3">
            <CheckCircle2 size={14} /> Maintenance log submitted
          </div>
          <h2 className="vb-heading text-xl mb-1" style={{ color: "var(--vb-text-2)" }}>
            Sent for supervisor sign-off — {equipmentName}
          </h2>
          <p className="text-sm opacity-80 mb-4">
            {siteName || "—"}{facilityName ? ` · ${facilityName}` : ""}
            {serialNumber ? ` · S/N ${serialNumber}` : ""}
          </p>
          <div className="text-xs opacity-70 mb-4 flex items-start gap-2">
            <ShieldCheck size={14} className="mt-0.5 shrink-0" />
            <span>
              Your log is now queued for supervisor verification. A supervisor
              will review and sign this record from the Cleaning &amp;
              Maintenance tab — just like the hygiene &amp; cleanliness flow.
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {onRescan && (
              <button className="vb-btn" onClick={onRescan} data-testid="maintenance-scan-next">
                <Camera size={16} /> Scan next equipment
              </button>
            )}
            {onHome && (
              <button className="vb-btn ghost" onClick={onHome} data-testid="maintenance-done">
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const staffName = user?.name || user?.full_name || user?.email || "—";

  return (
    <div className="max-w-lg vb-fade-in" data-testid="maintenance-form">
      {onBack && (
        <button onClick={onBack} className="vb-btn ghost mb-3" data-testid="maintenance-back">
          <ArrowLeft size={14} /> Back
        </button>
      )}

      <div className="vb-card p-5 mb-4">
        <div className="text-xs uppercase tracking-wider opacity-60 mb-1">
          <Wrench size={12} className="inline-block mr-1" /> Maintenance log
        </div>
        <div className="vb-heading text-2xl mb-1" style={{ color: "var(--vb-text-2)" }}
          data-testid="maintenance-eq-name">
          {equipmentName}
        </div>
        <div className="text-sm opacity-80">
          {siteName || "—"}
          {facilityName ? ` · ${facilityName}` : ""}
          {serialNumber ? ` · S/N ${serialNumber}` : ""}
        </div>
      </div>

      {err && (
        <div className="vb-pill err mb-3" data-testid="maintenance-error">{err}</div>
      )}

      <form onSubmit={submit} className="vb-card p-5 grid gap-4">
        {/* Prefilled to now but editable — an operator logging a job
            they finished earlier in the shift can back-date it. */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] font-semibold mb-1 uppercase tracking-wider opacity-60">
              Logged date
            </label>
            <input type="date" className="vb-input" required value={date}
              max={todayDate()}
              onChange={(e) => setDate(e.target.value)}
              data-testid="maintenance-date" />
          </div>
          <div>
            <label className="block text-[10px] font-semibold mb-1 uppercase tracking-wider opacity-60">
              Logged time
            </label>
            <input type="time" className="vb-input" required value={time}
              onChange={(e) => setTime(e.target.value)}
              data-testid="maintenance-time" />
          </div>
        </div>

        <div className="rounded-lg p-3" style={{ background: "var(--vb-bg)" }}>
          <div className="text-[10px] font-semibold uppercase tracking-wider opacity-60 mb-0.5">
            Performed by
          </div>
          <div className="text-sm font-medium" data-testid="maintenance-user">
            {staffName}
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
            style={{ color: "var(--vb-text-2)" }}>Reason for change</label>
          <select className="vb-input" value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="maintenance-reason">
            {REASON_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>

        <YesNoCheck
          testid="sanitation"
          label="Sanitation"
          help="Blade, guide arms and food-contact surfaces sanitised per protocol."
          value={sanitationOk}
          onChange={setSanitationOk}
          issue={sanitationIssue}
          onIssueChange={setSanitationIssue}
        />

        <YesNoCheck
          testid="tension"
          label="Tracking & tension verified"
          help="Blade tracking centred on the wheels and tension set to spec."
          value={tensionOk}
          onChange={setTensionOk}
          issue={tensionIssue}
          onIssueChange={setTensionIssue}
        />

        <div>
          <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
            style={{ color: "var(--vb-text-2)" }}>
            Notes <span className="opacity-50">(optional)</span>
          </label>
          <textarea className="vb-input" rows={2} value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="maintenance-notes" />
        </div>

        <div>
          <div className="text-xs font-semibold mb-1.5 uppercase tracking-wider"
            style={{ color: "var(--vb-text-2)" }}>
            Photos <span className="opacity-50">(optional)</span>
          </div>
          <div className="flex flex-wrap items-start gap-2">
            {photos.map((url, i) => (
              <div key={i} className="relative">
                <img src={url} alt="" className="w-16 h-16 rounded object-cover border" />
                <button type="button"
                  className="absolute -top-2 -right-2 vb-btn ghost xs"
                  onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))}
                  data-testid={`maintenance-photo-remove-${i}`}
                  style={{ padding: 2 }}>
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
            <label className="vb-btn ghost sm" style={{ cursor: "pointer" }}
              data-testid="maintenance-photo-add">
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
              Add photo
              <input type="file" accept="image/*" hidden
                onChange={(e) => uploadPhoto(e.target.files?.[0])} />
            </label>
          </div>
        </div>

        <div className="text-[11px] opacity-70 flex items-start gap-1.5">
          <ShieldCheck size={12} className="mt-0.5 shrink-0" />
          <span>
            After you submit, this log is queued for supervisor
            verification — a supervisor will sign it from the Cleaning &amp;
            Maintenance tab.
          </span>
        </div>

        <div className="flex justify-end">
          <button type="submit" className="vb-btn" disabled={busy}
            data-testid="maintenance-submit">
            {busy ? "Submitting…" : "Submit for supervisor sign-off"}
          </button>
        </div>
      </form>
    </div>
  );
}
